"use client";

/**
 * LiquidSurface
 * ─────────────
 * Primitivo de superficie del design language v2.
 *
 * Modo ESTATICO (default): solo pinta el "vidrio liquido" en una variante
 * de color. Sin transiciones, sin overlays, sin pseudos animados, sin
 * estado React, sin listeners. Pensado para cards, badges decorativos,
 * banners, showcase de paletas, etc.
 *
 * Modo INTERACTIVO (opt-in con `interactive` o `onClick`): activa el mismo
 * paquete de feedback del LiquidPill — cross-fade del surface via capa
 * overlay, animacion liquid de squish, warp y refraction en pseudos,
 * transicion de color/shadow/filter, cursor pointer, focus ring.
 *
 * Para superficies con comportamientos compuestos (dropdowns, expansion,
 * morph entre estados) usa LiquidPill y sus composites
 * (LiquidPillDropdown, LiquidPillExpandButton, LiquidPillGroup, ...).
 *
 * Reglas de uso:
 *   · La variante define el color tintado.
 *   · El tono "neutral" pinta el surface translucido (sin tint base).
 *   · El tono "tinted" pinta el surface con el tint base de la variante.
 *   · La forma define el border-radius.
 *   · El size aplica un padding/font preset; usa "free" para layout libre.
 *   · `interactive` activa hover/focus/squish y la capa de cross-fade.
 *
 * Nunca declares aqui:
 *   · estado React propio
 *   · z-index
 *   · positioning fixed/absolute
 * Si necesitas algo de eso, compon un composite en /v2/* siguiendo la
 * plantilla canonica del design language.
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import styles from "./liquid-surface.module.css";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";

export type LiquidDeformAxis = "both" | "horizontal" | "vertical";

const DEFORM_AXIS_WEIGHT: Record<LiquidDeformAxis, { x: number; y: number }> = {
  both: { x: 1, y: 1 },
  horizontal: { x: 1, y: 0 },
  vertical: { x: 0, y: 1 },
};

function applyDeformAxis(
  deformX: number,
  deformY: number,
  axis: LiquidDeformAxis,
): { x: number; y: number } {
  const weight = DEFORM_AXIS_WEIGHT[axis];
  return { x: deformX * weight.x, y: deformY * weight.y };
}

function updateLiquidSurfaceParticle(
  node: HTMLElement,
  clientX: number,
  clientY: number,
  deformAxis: LiquidDeformAxis = "both",
) {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  const x = ((clientX - rect.left) / rect.width) * 100;
  const y = ((clientY - rect.top) / rect.height) * 100;
  const minDim = Math.min(rect.width, rect.height);
  // Mantiene la misma proporción visual que una pill lg:
  // lg = 44px → partícula ≈ 88px → ratio 2x.
  const starSize = Math.max(20, Math.min(200, minDim * 2));

  const rawDeformX = Math.max(-1, Math.min(1, (x - 50) / 50));
  const rawDeformY = Math.max(-1, Math.min(1, (y - 50) / 50));
  const { x: deformX, y: deformY } = applyDeformAxis(rawDeformX, rawDeformY, deformAxis);

  node.style.setProperty("--ls-particle-x", `${x.toFixed(2)}%`);
  node.style.setProperty("--ls-particle-y", `${y.toFixed(2)}%`);
  node.style.setProperty("--ls-star-size", `${starSize.toFixed(1)}px`);
  node.style.setProperty("--ls-deform-x", deformX.toFixed(3));
  node.style.setProperty("--ls-deform-y", deformY.toFixed(3));
  node.style.setProperty("--ls-deform-origin-x", `${x.toFixed(2)}%`);
  node.style.setProperty("--ls-deform-origin-y", `${y.toFixed(2)}%`);
  node.style.setProperty("--ls-cursor-opacity", "1");
}

function triggerLiquidSurfaceDeform(
  node: HTMLElement,
  clientX: number,
  clientY: number,
  intensity: Exclude<LiquidSurfaceBounceIntensity, "auto"> = "medium",
  deformAxis: LiquidDeformAxis = "both",
) {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  const factors = getBounceFactors(intensity);
  if (factors.compress === 0) {
    // Sin rebote, solo registrar posición
    node.removeAttribute("data-ls-deforming");
    return;
  }

  const x = ((clientX - rect.left) / rect.width) * 100;
  const y = ((clientY - rect.top) / rect.height) * 100;
  const rawDeformX = Math.max(-1, Math.min(1, (x - 50) / 50));
  const rawDeformY = Math.max(-1, Math.min(1, (y - 50) / 50));
  const { x: deformX, y: deformY } = applyDeformAxis(rawDeformX, rawDeformY, deformAxis);

  node.style.setProperty("--ls-deform-x", deformX.toFixed(3));
  node.style.setProperty("--ls-deform-y", deformY.toFixed(3));

  // Anclamos la transformación al lado opuesto del punto de entrada.
  // Al comprimir desde ese ancla, el borde por donde entra el mouse se hunde.
  node.style.setProperty("--ls-deform-anchor-x", `${(100 - x).toFixed(2)}%`);
  node.style.setProperty("--ls-deform-anchor-y", `${(100 - y).toFixed(2)}%`);

  const absX = Math.abs(deformX);
  const absY = Math.abs(deformY);

  // Rebote direccional: prioriza el eje realmente afectado por el borde de entrada.
  // Si entras por izquierda/derecha, Y casi no rebota. Si entras por arriba/abajo,
  // X casi no rebota.
  const dominantAxis = absX >= absY ? "x" : "y";
  const primaryForce = Math.max(0.32, dominantAxis === "x" ? absX : absY);
  const secondaryForce = (dominantAxis === "x" ? absY : absX) * 0.18;

  const forceX = (dominantAxis === "x" ? primaryForce : secondaryForce) * DEFORM_AXIS_WEIGHT[deformAxis].x;
  const forceY = (dominantAxis === "y" ? primaryForce : secondaryForce) * DEFORM_AXIS_WEIGHT[deformAxis].y;

  // Usar factores parametrizados
  const compressFactorX = factors.compress;
  const compressFactorY = factors.compress * 0.80; // Y siempre un poco menos que X
  const reboundFactor = factors.rebound;
  const settleFactor = factors.settle;

  const compressX = 1 - forceX * compressFactorX;
  const compressY = 1 - forceY * compressFactorY;
  const reboundX = 1 + (1 - compressX) * reboundFactor;
  const reboundY = 1 + (1 - compressY) * (reboundFactor * 0.91); // Y rebota un poco menos
  const settleX = 1 - (1 - compressX) * settleFactor;
  const settleY = 1 - (1 - compressY) * (settleFactor * 0.90);

  node.style.setProperty("--ls-deform-scale-x", compressX.toFixed(3));
  node.style.setProperty("--ls-deform-scale-y", compressY.toFixed(3));
  node.style.setProperty("--ls-deform-rebound-scale-x", reboundX.toFixed(3));
  node.style.setProperty("--ls-deform-rebound-scale-y", reboundY.toFixed(3));
  node.style.setProperty("--ls-deform-settle-scale-x", settleX.toFixed(3));
  node.style.setProperty("--ls-deform-settle-scale-y", settleY.toFixed(3));

  node.removeAttribute("data-ls-deforming");
  // Reinicia la animación si el usuario re-entra rápido.
  void node.offsetWidth;
  node.setAttribute("data-ls-deforming", "true");
}

const CLICK_DEFORM_MS = 340;
const clickDeformTimers = new WeakMap<HTMLElement, number>();

function triggerLiquidSurfaceClickDeform(
  node: HTMLElement,
  clientX: number,
  clientY: number,
  intensity: Exclude<LiquidSurfaceBounceIntensity, "auto"> = "medium",
) {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  const x = ((clientX - rect.left) / rect.width) * 100;
  const y = ((clientY - rect.top) / rect.height) * 100;
  const minDim = Math.min(rect.width, rect.height);
  const rippleSize = Math.max(30, Math.min(140, minDim * 1.15));

  node.style.setProperty("--ls-deform-x", "0");
  node.style.setProperty("--ls-deform-y", "0");
  node.style.setProperty("--ls-deform-origin-x", "50%");
  node.style.setProperty("--ls-deform-origin-y", "50%");
  node.style.setProperty("--ls-ripple-x", `${x.toFixed(2)}%`);
  node.style.setProperty("--ls-ripple-y", `${y.toFixed(2)}%`);
  node.style.setProperty("--ls-ripple-size", `${rippleSize.toFixed(1)}px`);

  const clickIntensity = intensity === "none" ? "soft" : intensity;

  const baseByIntensity: Record<Exclude<LiquidSurfaceBounceIntensity, "auto">, {
    squash: number;
    stretch: number;
  }> = {
    none: { squash: 0.036, stretch: 0.044 },
    subtle: { squash: 0.045, stretch: 0.054 },
    soft: { squash: 0.056, stretch: 0.068 },
    medium: { squash: 0.068, stretch: 0.082 },
    strong: { squash: 0.084, stretch: 0.100 },
  };

  const base = baseByIntensity[clickIntensity];
  node.style.setProperty("--ls-click-squash", base.squash.toFixed(3));
  node.style.setProperty("--ls-click-stretch", base.stretch.toFixed(3));

  const prevTimer = clickDeformTimers.get(node);
  if (prevTimer != null) {
    window.clearTimeout(prevTimer);
  }

  node.removeAttribute("data-ls-deforming");
  node.removeAttribute("data-ls-clicking");
  void node.offsetWidth;
  node.setAttribute("data-ls-clicking", "true");
  node.setAttribute("data-ls-click-lock", "true");

  const timer = window.setTimeout(() => {
    node.removeAttribute("data-ls-clicking");
    clickDeformTimers.delete(node);
  }, CLICK_DEFORM_MS);
  clickDeformTimers.set(node, timer);
}

function hideLiquidSurfaceParticle(node: HTMLElement) {
  const prevTimer = clickDeformTimers.get(node);
  if (prevTimer != null) {
    window.clearTimeout(prevTimer);
    clickDeformTimers.delete(node);
  }

  node.style.setProperty("--ls-cursor-opacity", "0");
  node.style.setProperty("--ls-deform-x", "0");
  node.style.setProperty("--ls-deform-y", "0");
  node.style.setProperty("--ls-deform-origin-x", "50%");
  node.style.setProperty("--ls-deform-origin-y", "50%");
  node.removeAttribute("data-ls-deforming");
  node.removeAttribute("data-ls-clicking");
  node.removeAttribute("data-ls-click-lock");
}

export type LiquidSurfaceShape =
  | "pill"     // border-radius 9999px (clampea a min(50%, ...))
  | "rounded"  // border-radius 20px
  | "sheet"    // border-radius 16px (paneles grandes)
  | "chip"     // border-radius 12px (chips/labels)
  | "circle";  // border-radius 50%

export type LiquidSurfaceTone =
  | "neutral"  // surface translucido sin color (idle look del design system)
  | "tinted";  // surface con tint de la variante (active look del design system)

export type LiquidSurfaceSize =
  | "xxs"  // padding 2px 6px,  text 9px
  | "xs"   // padding 4px 8px,  text 10px
  | "sm"   // padding 6px 12px, text 11px
  | "md"   // padding 8px 14px, text 12px
  | "lg"   // padding 10px 16px, text 13px
  | "xl"   // padding 12px 20px, text 14px
  | "2xl"  // padding 14px 24px, text 16px
  | "free"; // sin padding/font preset (layout controlado externamente)

export type LiquidSurfaceBounceIntensity =
  | "none"    // sin efecto físico
  | "subtle"  // rebote mínimo, ideal para elementos pequeños
  | "soft"    // rebote suave, pills y chips
  | "medium"  // rebote estándar (default actual)
  | "strong"  // rebote pronunciado, botones destacados
  | "auto";   // calcula según el tamaño del elemento

/** Factores de rebote por intensidad: [compress, rebound, settle] */
const BOUNCE_FACTORS: Record<Exclude<LiquidSurfaceBounceIntensity, "auto">, {
  compress: number;
  rebound: number;
  settle: number;
}> = {
  none:   { compress: 0,     rebound: 0,    settle: 0    },
  subtle: { compress: 0.020, rebound: 0.12, settle: 0.03 },
  soft:   { compress: 0.032, rebound: 0.19, settle: 0.05 },
  medium: { compress: 0.048, rebound: 0.25, settle: 0.07 },
  strong: { compress: 0.068, rebound: 0.32, settle: 0.09 },
};

/** Mapeo de tamaño a intensidad de rebote por defecto */
const SIZE_TO_BOUNCE: Record<LiquidSurfaceSize, Exclude<LiquidSurfaceBounceIntensity, "auto">> = {
  xxs:  "subtle",
  xs:   "subtle",
  sm:   "soft",
  md:   "medium",
  lg:   "medium",
  xl:   "strong",
  "2xl": "strong",
  free: "medium",
};

/** Resuelve la intensidad final de rebote */
export function resolveBounceIntensity(
  bounce: LiquidSurfaceBounceIntensity | undefined,
  size: LiquidSurfaceSize | undefined
): Exclude<LiquidSurfaceBounceIntensity, "auto"> {
  if (!bounce || bounce === "auto") {
    return SIZE_TO_BOUNCE[size ?? "md"];
  }
  return bounce;
}

/** Obtiene los factores de rebote */
export function getBounceFactors(intensity: Exclude<LiquidSurfaceBounceIntensity, "auto">) {
  return BOUNCE_FACTORS[intensity];
}

/** Multiplicador del squish de burbuja en LiquidPillGroup / GroupVertical. */
const GROUP_SQUISH_INTENSITY: Record<Exclude<LiquidSurfaceBounceIntensity, "auto">, number> = {
  none: 0,
  subtle: 0.40,
  soft: 0.58,
  medium: 0.75,
  strong: 1.15,
};

export function resolveGroupBounceIntensity(
  intensity: LiquidSurfaceBounceIntensity = "strong",
): Exclude<LiquidSurfaceBounceIntensity, "auto"> {
  if (!intensity || intensity === "auto") return "strong";
  return intensity;
}

export function groupSquishStyle(
  intensity: LiquidSurfaceBounceIntensity = "strong",
  axis: LiquidDeformAxis = "both",
): React.CSSProperties {
  const resolved = resolveGroupBounceIntensity(intensity);
  const weight = DEFORM_AXIS_WEIGHT[axis];
  return {
    ["--lp-squish-intensity" as string]: String(GROUP_SQUISH_INTENSITY[resolved]),
    ["--lp-squish-x" as string]: String(weight.x),
    ["--lp-squish-y" as string]: String(weight.y),
  };
}

export function shouldGroupSquish(intensity: LiquidSurfaceBounceIntensity = "strong"): boolean {
  return resolveGroupBounceIntensity(intensity) !== "none";
}

export type LiquidSurfaceShadow =
  | "default"  // sombra/elevacion propia del estado visual
  | "neutral"  // mantiene elevacion neutra aunque el surface este tinted
  | "floating"; // elevacion expandida y fuerte — FAB / elementos fijos al viewport

export type LiquidSurfaceAs =
  | "div"
  | "span"
  | "section"
  | "article"
  | "aside"
  | "header"
  | "footer"
  | "li"
  | "nav"
  | "button"
  | "a";

export type LiquidSurfaceEdgeStyle =
  | "default"
  | "softGlow"
  | "flat";

export type LiquidSurfaceBodyStyle =
  | "default"
  | "flat";

export type LiquidSurfaceStyle =
  | "default"
  | "sidebarInactive"
  | "solidTranslucent"
  | "selectionSolid"
  | "switchTrackRecessed"
  | "switchTrackActive";

export interface LiquidSurfaceProps {
  /** Variante de color (14 opciones del design system). */
  variant?: CircleActionVariant;
  /** Tono del surface: neutral (sin tint) o tinted (con color). Default "tinted". */
  tone?: LiquidSurfaceTone;
  /** Forma / border-radius. Default "rounded". */
  shape?: LiquidSurfaceShape;
  /** Padding y font-size preset. Default "md". Usa "free" para control externo. */
  size?: LiquidSurfaceSize;
  /** Elemento HTML semantico. Default "div". */
  as?: LiquidSurfaceAs;
  /**
   * Activa el feedback de hover/focus: cross-fade del surface, squish liquid,
   * warp y refraction en pseudos, cursor pointer y focus ring. Default false.
   * Se activa automaticamente si `as="button"` o si se pasa `onClick`.
   */
  interactive?: boolean;
  /**
   * Controla solo la elevacion/sombra del surface. `neutral` permite que un
   * surface tinted conserve color/borde de variante sin ganar lift extra.
   * Usado por dropdowns/listas donde el selected no debe verse mas elevado.
   */
  shadow?: LiquidSurfaceShadow;
  /** Muestra la partícula especular que sigue/opone el mouse. Default true. */
  cursorGlow?: boolean;
  /** Fuerza visible la partícula aunque el surface no reciba hover directo. */
  cursorGlowVisible?: boolean;
  /** Activa el material reactivo al fondo (background/backdrop + borde). Default false. */
  reactive?: boolean;
  /** Activa rebote líquido en hover, incluso si el surface no es interactivo. */
  hoverBounce?: boolean;
  /** Deforma el material según la posición local del puntero durante hover. */
  hoverDeform?: boolean;
  /**
   * Intensidad del rebote líquido.
   * - none: sin efecto físico
   * - subtle: rebote mínimo (elementos pequeños)
   * - soft: rebote suave (pills, chips)
   * - medium: rebote estándar
   * - strong: rebote pronunciado (botones destacados)
   * - auto: calcula según el tamaño del elemento (default)
   */
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  /**
   * Eje de deformación al hover (`hoverDeform`) y referencia para composites.
   * - both: escala en X e Y (pill completa).
   * - horizontal: solo compresión/rebote en X.
   * - vertical: solo compresión/rebote en Y.
   */
  deformAxis?: LiquidDeformAxis;
  /**
   * Ajusta cómo se dibuja el borde/rim del glass.
   * - default: borde estándar del sistema.
   * - softGlow: borde más transparente + brillo suave.
   * - flat: borde casi imperceptible, look más plano.
   */
  edgeStyle?: LiquidSurfaceEdgeStyle;
  /**
   * Ajusta el cuerpo/gradiente del material.
   * - default: volumen glass estándar.
   * - flat: gradiente más uniforme y menos "inflado".
   */
  bodyStyle?: LiquidSurfaceBodyStyle;
  /**
   * Variante visual compuesta de surface.
   * - default: usa tone/bodyStyle/edgeStyle normales.
   * - sidebarInactive: en dark mode replica exactamente el shell inactivo del sidebar.
   * - solidTranslucent: no usa transparencia real, pero simula vidrio premium opaco.
   * - selectionSolid: estado seleccionado de escala/opción; más sólido y contrastado.
   * - switchTrackRecessed: carril off del LiquidSwitch (gris contrastado).
   * - switchTrackActive: carril on del LiquidSwitch (tinte oscuro de la variante).
   */
  surfaceStyle?: LiquidSurfaceStyle;
  /** Click handler (auto-activa interactive). */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Solo aplica si `as="button"`. Default "button". */
  type?: "button" | "submit" | "reset";
  /** Solo aplica si `as="a"`. */
  href?: string;
  /** Marca el surface como deshabilitado (apaga interactividad y atenua). */
  disabled?: boolean;
  /** TabIndex explicito (ignora el auto-asignado para roles no nativos). */
  tabIndex?: number;
  /** Contenido. */
  children?: React.ReactNode;
  /** Clases adicionales. */
  className?: string;
  /** Estilos inline adicionales (mezclados despues de las CSS vars). */
  style?: React.CSSProperties;
  /** Atributo aria-label para accesibilidad. */
  "aria-label"?: string;
  /** Estado expandido para botones que controlan flyouts/menús. */
  "aria-expanded"?: boolean;
  /** role ARIA opcional. */
  role?: string;
  /** Asocia el botón a un `<form id>` externo. */
  form?: string;
  name?: string;
  value?: string;
  id?: string;
  accessKey?: string;
}

const SHAPE_RADIUS: Record<LiquidSurfaceShape, string> = {
  pill: "rounded-full",
  rounded: "rounded-[20px]",
  sheet: "rounded-2xl",
  chip: "rounded-xl",
  circle: "rounded-full",
};

const SIZE_PADDING: Record<LiquidSurfaceSize, string> = {
  xxs: "px-1.5 py-0.5 text-[9px] leading-tight",
  xs: "px-2 py-1 text-[10px] leading-tight",
  sm: "px-3 py-1.5 text-[11px] leading-tight",
  md: "px-3.5 py-2 text-xs leading-snug",
  lg: "px-4 py-2.5 text-[13px] leading-snug",
  xl: "px-5 py-3 text-sm leading-normal",
  "2xl": "px-6 py-3.5 text-base leading-normal",
  free: "",
};

export const LiquidSurface = React.forwardRef<HTMLElement, LiquidSurfaceProps>(
  function LiquidSurface(
    {
      variant: variantProp,
      tone = "tinted",
      shape = "rounded",
      size = "md",
      as = "div",
      interactive: interactiveProp,
      shadow = "default",
      cursorGlow: cursorGlowProp = true,
      cursorGlowVisible = false,
      reactive = false,
      hoverBounce = false,
      hoverDeform = false,
      bounceIntensity = "auto",
      deformAxis = "both",
      edgeStyle = "default",
      bodyStyle = "default",
      surfaceStyle = "default",
      onClick,
      type = "button",
      href,
      disabled = false,
      tabIndex,
      children,
      className,
      style: styleProp,
      "aria-label": ariaLabel,
      "aria-expanded": ariaExpanded,
      role,
      form,
      name,
      value,
      id,
      accessKey,
    },
    ref,
  ) {
    const resolvedVariant = resolveCircleActionVariant(variantProp ?? "slate");
    const variantStyle = circleActionVariantCssVars(resolvedVariant);
    const rootRef = React.useRef<HTMLElement | null>(null);
    const resolvedBounce = resolveBounceIntensity(bounceIntensity, size);

    const setRootRef = React.useCallback((node: HTMLElement | null) => {
      rootRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    }, [ref]);

    // Auto-activa interactive si el consumer pasa onClick o usa as="button"/"a".
    const isInteractive =
      !disabled && (interactiveProp || as === "button" || as === "a" || Boolean(onClick));

    const rootClass = cn(
      "liquid-surface",
      styles.root,
      "relative isolation-auto overflow-hidden",
      // Sin transitions ni cursor en idle puro; los aplica el stylesheet
      // global cuando data-interactive="true" para mantener la regla:
      // el surface estatico no tiene reglas de movimiento, solo cuando
      // se activa interactive.
      isInteractive && [
        "cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1",
        disabled && "pointer-events-none opacity-40",
      ],
      SHAPE_RADIUS[shape],
      SIZE_PADDING[size],
      className,
    );

    const mergedStyle: React.CSSProperties = {
      ...(variantStyle as React.CSSProperties),
      ...styleProp,
    };

    const dataAttributes: Record<string, string> = {
      "data-shape": shape,
      "data-tone": tone,
      "data-variant": resolvedVariant,
      "data-shadow": shadow,
    };
    if (cursorGlowVisible) dataAttributes["data-cursor-glow"] = "visible";
    if (reactive) dataAttributes["data-reactive"] = "true";
    if (isInteractive) dataAttributes["data-interactive"] = "true";
    if (hoverBounce) dataAttributes["data-hover-bounce"] = "true";
    if (hoverDeform) dataAttributes["data-hover-deform"] = "true";
    if (edgeStyle !== "default") dataAttributes["data-edge-style"] = edgeStyle;
    if (bodyStyle !== "default") dataAttributes["data-body-style"] = bodyStyle;
    if (surfaceStyle !== "default") dataAttributes["data-surface-style"] = surfaceStyle;

    // Overlay siempre montado. Su opacidad la decide el CSS segun:
    //   · tone="tinted"  → opacity 1 estatico (water-drop 3D permanente)
    //   · interactive + :hover/:focus-visible → opacity 0→1 (cross-fade)
    //   · tone="neutral" sin interactivo → opacity 0 (invisible, DOM minimo)
    // No lo gateamos por isInteractive porque eso rompia el look 3D de los
    // surfaces estaticos tinted (la burbuja del LiquidPillGroup, el panel
    // del LiquidPillDropdown, badges decorativos tinted, etc.).
    const hoverOverlay = (
      <span
        aria-hidden="true"
        className={cn("liquid-surface__hover-overlay", styles.hoverOverlay)}
      />
    );

    const reactiveRing = reactive ? (
      <span
        aria-hidden="true"
        className={cn("liquid-surface__reactive-ring", styles.reactiveRing)}
      />
    ) : null;

    const cursorGlow = cursorGlowProp ? (
      <span
        aria-hidden="true"
        className={cn("liquid-surface__cursor-glow", styles.cursorGlow)}
      />
    ) : null;

    const clickRipple = (
      <span
        aria-hidden="true"
        className={cn("liquid-surface__click-ripple", styles.clickRipple)}
      />
    );

    const handlePointerEnter = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
      if (!hoverDeform) return;
      const node = event.currentTarget;
      updateLiquidSurfaceParticle(node, event.clientX, event.clientY, deformAxis);
      triggerLiquidSurfaceDeform(node, event.clientX, event.clientY, resolvedBounce, deformAxis);
    }, [hoverDeform, resolvedBounce, deformAxis]);

    const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
      if (!cursorGlowProp) return;
      updateLiquidSurfaceParticle(event.currentTarget, event.clientX, event.clientY, deformAxis);
    }, [cursorGlowProp, deformAxis]);

    const handlePointerLeave = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
      if (!cursorGlowProp && !hoverDeform) return;
      hideLiquidSurfaceParticle(event.currentTarget);
    }, [cursorGlowProp, hoverDeform]);

    const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
      if (disabled) return;
      triggerLiquidSurfaceClickDeform(
        event.currentTarget,
        event.clientX,
        event.clientY,
        resolvedBounce,
      );
    }, [disabled, resolvedBounce]);

    // Polimorfismo via createElement: una sola rama, ref forwarding correcto,
    // y filtrado de props que no aplican al tag elegido.
    const elementProps: Record<string, unknown> = {
      ref: setRootRef,
      className: rootClass,
      style: mergedStyle,
      "aria-label": ariaLabel,
      "aria-expanded": ariaExpanded,
      role,
      tabIndex,
      onPointerEnter: handlePointerEnter,
      onPointerMove: handlePointerMove,
      onPointerLeave: handlePointerLeave,
      onPointerDown: handlePointerDown,
      ...dataAttributes,
    };

    if (isInteractive) {
      elementProps.onClick = onClick;
    }
    if (as === "button") {
      elementProps.type = type;
      elementProps.disabled = disabled;
      if (form != null) elementProps.form = form;
      if (name != null) elementProps.name = name;
      if (value != null) elementProps.value = value;
      if (accessKey != null) elementProps.accessKey = accessKey;
    }
    if (id != null) elementProps.id = id;
    if (as === "a") {
      elementProps.href = href;
      if (disabled) elementProps["aria-disabled"] = true;
    }

    return React.createElement(
      as,
      elementProps,
      <>
        {hoverOverlay}
        {reactiveRing}
        {cursorGlow}
        {clickRipple}
        {children}
      </>,
    );
  },
);

LiquidSurface.displayName = "LiquidSurface";
